import React from 'react';
import { Box, Stack, Chip, Typography, Button } from '@mui/material';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { RPMText } from './subComponents/RPMText';
import { detectLang, DEFAULT_LANG } from '../utils/language';

// End-of-article signup CTA copy, keyed by article language (falls back to English).
const CTA_COPY = {
    sv: {
        heading: 'Kom igång med Cardcam',
        subtext: 'Skapa ditt digitala visitkort och börja samla in leads – på några minuter.',
        button: 'Skapa konto',
    },
    en: {
        heading: 'Get started with Cardcam',
        subtext: 'Create your digital business card and start collecting leads — in minutes.',
        button: 'Create account',
    },
};

const DefaultPostRenderer = ({ postData, colorTheme = 'white' }) => {
    const px = { xs: 2, md: 4 };
    const pt = { xs: 2, md: 4 };
    const pb = { xs: 2, md: 4 };

    // Extract post title from content
    const getPostTitle = () => {
        if (!postData?.content?.rendered) return postData?.title?.rendered || '';

        const parser = new DOMParser();
        const doc = parser.parseFromString(postData.content.rendered, 'text/html');
        const h1 = doc.querySelector('h1');

        if (h1) {
            // Remove h1 from content since we'll render it separately
            h1.remove();
            return h1.textContent;
        }

        return postData?.title?.rendered || '';
    };

    const title = getPostTitle();

    // Get content without the h1 tag
    const getContentWithoutH1 = () => {
        if (!postData?.content?.rendered) return '';

        const parser = new DOMParser();
        const doc = parser.parseFromString(postData.content.rendered, 'text/html');
        const h1 = doc.querySelector('h1');

        if (h1) {
            h1.remove();
        }

        return doc.body.innerHTML;
    };

    const contentWithoutTitle = getContentWithoutH1();

    // Localized signup CTA — the URL carries the article's language (e.g. /sv/signup).
    const lang = typeof window !== 'undefined' ? detectLang(window.location.pathname) : DEFAULT_LANG;
    const cta = CTA_COPY[lang] || CTA_COPY.en;
    const signupUrl = `https://b.cardcam.io/${lang}/signup`;

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('sv-SE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <Box
            className="slice default-post"
            px={px}
            pt={pt}
            pb={pb}
            sx={{
                background: (theme) => theme.palette.background.default,
                minHeight: '100vh'
            }}
        >
                {/* Post Header */}
                <Stack direction="column" alignItems="center" spacing={2} sx={{ mb: 4, mt: 8 }}>
                    {title && (
                        <RPMText content={title} variant="h1" component="h1" />
                    )}

                    {/* Post Meta */}
                    <Stack direction="row" spacing={2} alignItems="center">
                        {postData?.date && (
                            <Chip
                                label={formatDate(postData.date)}
                                variant="outlined"
                                size="small"
                                sx={{
                                    color: 'text.secondary',
                                    borderColor: 'divider'
                                }}
                            />
                        )}

                    </Stack>

                    {/* Featured Image */}
                    {postData?.featured_media_url && (
                        <Box
                            component="img"
                            src={postData.featured_media_url}
                            alt={postData?.featured_media_alt || title}
                            sx={{
                                width: '100%',
                                maxWidth: 800,
                                height: 'auto',
                                borderRadius: 2,
                                boxShadow: 2,
                                mt: 2
                            }}
                        />
                    )}
                </Stack>

                {/* Post Content */}
                <Box
                    sx={{
                        maxWidth: { sm: "100%", lg: "800px" }, // Narrower than pages for better readability, with responsive breakpoints
                        mx: 'auto',
                        '& h2': {
                            fontSize: { xs: '1.5rem', md: '2rem' },
                            fontWeight: 600,
                            color: 'text.primary',
                            mt: 4,
                            mb: 3
                        },
                        '& h3': {
                            fontSize: { xs: '1.25rem', md: '1.5rem' },
                            fontWeight: 500,
                            color: 'text.primary',
                            mt: 3,
                            mb: 2
                        },
                        '& h4, & h5, & h6': {
                            fontWeight: 500,
                            color: 'text.primary',
                            mt: 2,
                            mb: 2
                        },
                        '& p': {
                            color: 'text.secondary',
                            mb: 3,
                            lineHeight: 1.8, // Slightly more line height for posts
                            fontSize: { xs: '1rem', md: '1.125rem' }
                        },
                        '& p strong, & strong': {
                            color: 'text.primary',
                            fontWeight: 600
                        },
                        '& a': {
                            color: 'primary.main',
                            textDecoration: 'underline',
                            '&:hover': {
                                textDecoration: 'none'
                            }
                        },
                        '& ul, & ol': {
                            color: 'text.secondary',
                            mb: 3,
                            pl: 3,
                            lineHeight: 1.8,
                            '& li': {
                                mb: 1
                            }
                        },
                        '& blockquote': {
                            borderLeft: '4px solid',
                            borderColor: 'primary.main',
                            pl: 3,
                            ml: 0,
                            fontStyle: 'italic',
                            color: 'text.secondary',
                            backgroundColor: 'background.paper',
                            py: 3,
                            my: 4,
                            fontSize: '1.125rem'
                        },
                        '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: 2,
                            my: 3
                        },
                        '& pre': {
                            backgroundColor: 'background.paper',
                            p: 3,
                            borderRadius: 1,
                            overflow: 'auto',
                            fontSize: '0.9rem'
                        }
                    }}
                    dangerouslySetInnerHTML={{ __html: contentWithoutTitle }}
                />

                {/* End-of-article signup CTA */}
                <Box sx={{ maxWidth: { sm: '100%', lg: '800px' }, mx: 'auto', mt: { xs: 6, md: 8 } }}>
                    <Box
                        sx={{
                            borderRadius: 2,
                            p: { xs: 4, md: 6 },
                            textAlign: 'center',
                            background: 'linear-gradient(135deg, rgba(92,51,215,0.10), rgba(6,194,220,0.10))',
                            border: '1px solid rgba(92,51,215,0.15)',
                        }}
                    >
                        <Typography sx={{ fontSize: { xs: '1.5rem', md: '1.9rem' }, fontWeight: 700, color: 'text.primary', mb: 1.5 }}>
                            {cta.heading}
                        </Typography>
                        <Typography sx={{ fontSize: { xs: '1rem', md: '1.125rem' }, color: 'text.secondary', mb: 3, maxWidth: 560, mx: 'auto', lineHeight: 1.6 }}>
                            {cta.subtext}
                        </Typography>
                        <Button
                            variant="contained"
                            href={signupUrl}
                            endIcon={<KeyboardDoubleArrowRightIcon />}
                            sx={{
                                backgroundColor: '#5C33D7',
                                '&:hover': { backgroundColor: '#4C1D95' },
                                color: '#fff',
                                textTransform: 'none',
                                borderRadius: '0.5rem',
                                px: 4,
                                py: 1.5,
                                fontSize: '1.05rem',
                                fontWeight: 600,
                            }}
                        >
                            {cta.button}
                        </Button>
                    </Box>
                </Box>
        </Box>
    );
};

export default DefaultPostRenderer;